import { useGetCompanySpecificIndustryList, useGetNationalityList } from "@/api/services/global/master";
import { Button } from "@/components/elements/Button";
import { DebounceInput } from "@/components/elements/Input/Input";
import { Select } from "@/components/elements/Select";
import { Box, Grid, Stack, Typography } from "@mui/material";
import React, { FC, useMemo, ChangeEvent } from "react";
import { AppRoutes } from "@/routes/config";
import { Link } from "react-router-dom";
import { black, divider } from "@/theme/colors";
import { Tooltip } from "@/components/elements/Tooltip";
import { IconErrorOutline, IconFileExport, IconFileImport, IconPlus } from "@/assets/icons";
import { TPagination } from "@/types/api";
import { useEnum } from "@/api/services/global/enum";
import { ImportTalentModal } from "@/features/talent/components/talent-import/ImportTalentModal";
import { useVisible } from "@/hooks/useVisible";
import { normalizeKeyword } from "@/utils/string";

type TTalentFilterForm = {
  keyword: string;
  nationalityId: number;
  companySpecificIndustryId: number;
  recruitmentStatus: number;
};

type Props = {
  formValues: TTalentFilterForm;
  onChangeFormValues: (vals: TTalentFilterForm | ((prev: TTalentFilterForm) => TTalentFilterForm)) => void;
  onSubmit: (vals: TTalentFilterForm) => void;
  pagination?: TPagination;
  setIsExport: (val: boolean) => void;
};

export const TalentListTop: FC<Props> = ({ formValues, onChangeFormValues, onSubmit, pagination, setIsExport }) => {
  const { nationalityOptions } = useGetNationalityList();
  const { companySpecificIndustryOptions } = useGetCompanySpecificIndustryList();
  const { data: recruitmentStatusOptions } = useEnum("ERecruitmentStatus");

  const importPersonnelModal = useVisible();

  const defaultValues: TTalentFilterForm = {
    keyword: "",
    nationalityId: 0,
    companySpecificIndustryId: 0,
    recruitmentStatus: 0,
  };

  const isSubmitEnabled = useMemo(() => {
    return Boolean(formValues.keyword || formValues.nationalityId || formValues.companySpecificIndustryId || formValues.recruitmentStatus);
  }, [formValues]);

  const isAllEmpty = !formValues.keyword && !formValues.nationalityId && !formValues.companySpecificIndustryId && !formValues.recruitmentStatus;

  const formatTotal = (total: number = 0) => {
    return total.toLocaleString("en-US");
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChangeFormValues((prev) => ({
      ...prev,
      keyword: e.target.value,
    }));
  };

  const handleClear = () => {
    onChangeFormValues(defaultValues);
    onSubmit(defaultValues);
  };

  const handleExport = () => {
    setIsExport(true);
  };

  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md>
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="cap12Light">キーワード検索</Typography>
              <Tooltip title="以下から検索ができます。" content="名前、呼び名、パートナー名、職歴、学歴、資格、日本語能力">
                <Box component="span" sx={{ display: "inline-flex", verticalAlign: "middle", lineHeight: 0 }}>
                  <IconErrorOutline width={15} height={15} color={black[80]} />
                </Box>
              </Tooltip>
            </Stack>
            <DebounceInput
              placeholder="入力してください"
              value={formValues.keyword}
              onChange={handleInputChange}
              size="md"
              style={{ width: "100%" }}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md>
          <Stack spacing={1}>
            <Typography variant="cap12Light">国籍</Typography>
            <Select
              placeholder="選択してください"
              value={formValues.nationalityId}
              onChange={(value) => {
                const newValue = value === "" || value === null || value === undefined ? 0 : Number(value);
                onChangeFormValues((prev) => ({
                  ...prev,
                  nationalityId: newValue,
                }));
              }}
              options={nationalityOptions}
              size="md"
              style={{ width: "100%" }}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md>
          <Stack spacing={1}>
            <Typography variant="cap12Light">分野</Typography>
            <Select
              placeholder="選択してください"
              value={formValues.companySpecificIndustryId}
              onChange={(value) => {
                const newValue = value === "" || value === null || value === undefined ? 0 : Number(value);
                onChangeFormValues((prev) => ({
                  ...prev,
                  companySpecificIndustryId: newValue,
                }));
              }}
              options={companySpecificIndustryOptions}
              size="md"
              style={{ width: "100%" }}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md>
          <Stack spacing={1}>
            <Typography variant="cap12Light">活動状況</Typography>
            <Select
              placeholder="選択してください"
              value={formValues.recruitmentStatus}
              onChange={(value) => {
                const newValue = value === "" || value === null || value === undefined ? 0 : Number(value);
                onChangeFormValues((prev) => ({
                  ...prev,
                  recruitmentStatus: newValue,
                }));
              }}
              options={recruitmentStatusOptions}
              size="md"
              style={{ width: "100%" }}
            />
          </Stack>
        </Grid>

        <Grid item xs="auto">
          <Stack spacing={1}>
            <Typography variant="cap12Light" sx={{ opacity: 0, lineHeight: "20px" }}>
              操作
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                type="button"
                variant="text"
                disabled={isAllEmpty}
                onClick={handleClear}
                size="sm"
                sx={{ marginLeft: "-20px !important", width: "74px" }}
              >
                クリア
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!isSubmitEnabled}
                sx={{ width: "60px" }}
                onClick={() => {
                  const normalizedValues = {
                    ...formValues,
                    keyword: normalizeKeyword(formValues.keyword),
                  };
                  onSubmit(normalizedValues);
                }}
              >
                <Typography variant="body14Semi">検索</Typography>
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>

      <Box sx={{ marginBottom: 2, marginTop: 2, color: divider.high, borderTop: "1px solid" }}></Box>

      <Box marginBottom={2} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body14Semi">
          全 {formatTotal(pagination?.total) ?? 0} 件中 {Math.min((pagination?.page ?? 1) * (pagination?.perPage ?? 20), pagination?.total ?? 0)}{" "}
          件を表示
        </Typography>
        <Box display="flex" gap={2}>
          <Button variant="outline" size="sm" startIcon={<IconFileExport />} onClick={handleExport} sx={{ width: "170px" }}>
            <Typography variant="body14Semi">Excelエクスポート</Typography>
          </Button>
          <Button onClick={importPersonnelModal.open} variant="outline" size="sm" startIcon={<IconFileImport />} sx={{ width: "156px" }}>
            <Typography variant="body14Semi">括インポート</Typography>
          </Button>
          <Button component={Link} to={AppRoutes.talentCreate} size="sm" sx={{ width: "144px" }} startIcon={<IconPlus width={20} height={20} />}>
            <Typography variant="body14Semi">新規人材登録</Typography>
          </Button>
        </Box>
      </Box>
      <ImportTalentModal modal={importPersonnelModal} />
    </>
  );
};
